import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';

import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ModalConfig } from '../models/modal-config.model';

/**
 * Generic service to manage modal actions in the application
 * Can be used in any part of the application to display modals in a standardized way
 */
@Injectable({
  providedIn: 'root'
})
export class ModalActionsService {
  readonly #dialog = inject(MatDialog);

  /**
   * Opens a modal based on the provided configuration
   * @param modalConfig - Configuration of the modal to be opened
   * @returns Observable that emits the result when the modal is closed
   */
  openModal<P, R>(modalConfig: ModalConfig<P>): Observable<R | any> {
    if (!modalConfig.component) return EMPTY;

    const defaultConfig = {
      autoFocus: false,
      closeOnNavigation: true,
      width: '680px',
      height: '100%',
      panelClass: 'right-modal'
    };

    const config = {
      ...defaultConfig,
      ...(modalConfig.dialogConfig || {}),
      data: modalConfig.params
    };

    const ref: MatDialogRef<any, R> = this.#dialog.open(modalConfig.component, config);

    return ref.afterClosed();
  }

  /**
   * Opens a modal and returns the dialog reference
   * @param modalConfig - Configuration of the modal to be opened
   * @returns MatDialogRef of the opened modal
   */
  openModalWithRef<P, R>(modalConfig: ModalConfig<P>): MatDialogRef<any, R> | null {
    if (!modalConfig.component) return null;

    const defaultConfig = {
      autoFocus: false,
      closeOnNavigation: true,
      width: '685px',
      height: '100%',
      panelClass: 'right-modal'
    };

    const config = {
      ...defaultConfig,
      ...(modalConfig.dialogConfig || {}),
      data: modalConfig.params
    };

    return this.#dialog.open(modalConfig.component, config);
  }
}
